import React from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { LineItem } from 'common/display/LineItem';
import { dollarAmount } from 'common/display/Money/DollarAmount';
import formatShortDate from 'src/common/utils/Dates/formatShortDate';

const BaseLineItem = ({ ...props }) => <LineItem valueClassName="m-0" {...props} />;

const formatAmount = (amount, unit) => (
  unit ?
  `${amount} ${pluralize(unit, amount)}` :
  dollarAmount(amount, true)
);

const AmountLineItem = ({
  currentAmount,
  fieldName,
  isUnitBased,
  previousAmount,
  unit,
}) => {
  const displayedAmount = formatAmount(currentAmount, isUnitBased && unit);

  let displayedPreviousAmount;
  if (previousAmount) {
    displayedPreviousAmount = formatAmount(previousAmount, isUnitBased && unit);
  }

  return (
    <BaseLineItem field={fieldName} dataTestElement="amount-requested">
      {displayedAmount}
      {displayedPreviousAmount && (
        <>
          {' '}
          <span className="line-through">{displayedPreviousAmount}</span>
        </>
      )}
    </BaseLineItem>
  );
};

AmountLineItem.propTypes = {
  currentAmount: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  isUnitBased: PropTypes.bool.isRequired,
  previousAmount: PropTypes.number,
  unit: PropTypes.string,
};

AmountLineItem.defaultProps = {
  previousAmount: null,
  unit: null,
};

const ShortDates = ({ className, startDate, endDate }) => (
  <span className={className}>
    {(startDate !== endDate) && `${formatShortDate(startDate)} - `}
    {formatShortDate(endDate)}
  </span>
);

ShortDates.propTypes = {
  className: PropTypes.string,
  endDate: PropTypes.string.isRequired,
  startDate: PropTypes.string,
};

ShortDates.defaultProps = {
  className: '',
  startDate: null,
};

const ServiceDeliveryDatesLineItem = ({
  endDate,
  fieldName,
  previousEndDate,
  previousStartDate,
  startDate,
  testElement,
}) => (
  <BaseLineItem field={fieldName} dataTestElement={testElement}>
    <ShortDates startDate={startDate} endDate={endDate} />
    {previousEndDate && (
      <>
        {' '}
        <ShortDates
          className="line-through"
          endDate={previousEndDate}
          startDate={previousStartDate}
        />
      </>
    )}
  </BaseLineItem>
);

ServiceDeliveryDatesLineItem.propTypes = {
  endDate: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  previousEndDate: PropTypes.string,
  previousStartDate: PropTypes.string,
  startDate: PropTypes.string.isRequired,
  testElement: PropTypes.string.isRequired,
};

ServiceDeliveryDatesLineItem.defaultProps = {
  previousEndDate: null,
  previousStartDate: null,
};

const ClinicalModificationsLineItem = ({ clinicalModifications, previousClinicalModifications }) => {
  const displayedClinicalModifications = clinicalModifications?.map((cm) => cm.code)?.join(',');
  const displayedPreviousClinicalModifications = previousClinicalModifications?.map((cm) => cm.code)?.join(',');
  return (
    <BaseLineItem field="ICD-10 Code">
      {displayedClinicalModifications}
      {previousClinicalModifications?.length > 0 && (
        <>
          {' '}
          <span className="line-through">{displayedPreviousClinicalModifications}</span>
        </>
      )}
    </BaseLineItem>
  );
};

const clinicalModificationPropType = PropTypes.shape({
  code: PropTypes.string.isRequired,
});

ClinicalModificationsLineItem.propTypes = {
  clinicalModifications: PropTypes.arrayOf(clinicalModificationPropType).isRequired,
  previousClinicalModifications: PropTypes.arrayOf(clinicalModificationPropType).isRequired,
};

const CapInformationLineItem = ({ capInformation }) => (
  <BaseLineItem field="Program Cap">
    {capInformation ? (
      /* eslint-disable-next-line react/no-danger */
      <div dangerouslySetInnerHTML={{ __html: capInformation }} />
    ) : (
      <span>No cap</span>
    )}
  </BaseLineItem>
);

CapInformationLineItem.propTypes = {
  capInformation: PropTypes.string,
};

CapInformationLineItem.defaultProps = {
  capInformation: '',
};

export {
  AmountLineItem,
  CapInformationLineItem,
  ClinicalModificationsLineItem,
  ServiceDeliveryDatesLineItem,
  BaseLineItem,
};
